import { graphqlCall } from '@pella/fetch-client';
import * as queries from './productionRequest.graphql.js';

const GRAPHQL_URL = `${window.REACT_APP_API_URL}/production-request/graphql`;

/**
 * Create one or more inventory-management transactions.
 * @param {Local.CreateInvMgmtTransactionInput} input
 * @returns {Promise<Local.CreateInvMgmtTransactionOutput>}
 */
export const createInvMgmtTransaction = async (input) => {
  const { data } = await graphqlCall(GRAPHQL_URL, queries.CREATE_INV_MGMT_TRANSACTION, { input });
  return data.productionRequestMutation.createInvMgmtTransaction;
};
/**
 * create one or more actions for a production request.
 * @param {Local.ActionMutationInput} input
 * @returns {Promise<Action>}
 */
export const createAction = async (input) => {
  const { data } = await graphqlCall(GRAPHQL_URL, queries.CREATE_ACTION_MUTATION, { input });
  return data.productionRequestMutation.createAction;
};
/**
 * Update an action for a production request.
 * @param {string} id
 * @param {Local.ActionMutationInput} input
 * @returns {Promise<Action>}
 */
export const updateAction = async (id, input) => {
  const { data } = await graphqlCall(GRAPHQL_URL, queries.UPDATE_ACTION_MUTATION, { id, input });
  return data.productionRequestMutation.updateActionById;
};
/**
 * get an action for a production request by id.
 * @param {string} id
 * @returns {Promise<Action>}
 */
export const getActionById = async (id) => {
  const { data } = await graphqlCall(GRAPHQL_URL, queries.GET_ACTION_BY_ID, { id });
  return data.productionRequestQuery.getActionById;
};
/**
 * Get the open requests (Available, Accepted, or On Hold) that already match an action and dynamic
 * input, so a user can be warned before submitting a duplicate.
 * @param {string} actionId - The action the requests were generated from
 * @param {string} dynamicInput - The dynamic input value to match, such as an item number
 * @returns {Promise<Local.OpenRequestSummary[]>}
 */
export const getOpenRequestsByActionIdAndDynamicInput = async (actionId, dynamicInput) => {
  const { data } = await graphqlCall(GRAPHQL_URL, queries.GET_OPEN_REQUESTS_BY_ACTION_ID_AND_DYNAMIC_INPUT, {
    actionId,
    dynamicInput,
  });
  return data.productionRequestQuery.getOpenRequestsByActionIdAndDynamicInput;
};
/**
 * Append a communication entry to a production request. The API builds the entry from the
 * caller's identity and returns the request carrying its full communication history.
 * @param {string!} requestId - The request the communication belongs to
 * @param {string!} communication - The communication text entered by the user
 * @returns {Promise<Local.RequestWithCommunication>}
 */
export const createCommunication = async (requestId, communication) => {
  const { data } = await graphqlCall(GRAPHQL_URL, queries.CREATE_COMMUNICATION_MUTATION, { requestId, communication });
  return data.productionRequestMutation.createCommunication;
};
/**
 * Mark every communication on a production request as read for the calling user. The API
 * records the read receipt and returns the request carrying its full communication history.
 * @param {string} requestId - The request whose communications are being marked read
 * @returns {Promise<Local.RequestWithCommunication>}
 */
export const markCommunicationAsRead = async (requestId) => {
  const { data } = await graphqlCall(GRAPHQL_URL, queries.MARK_COMMUNICATION_AS_READ_MUTATION, { requestId });
  return data.productionRequestMutation.markCommunicationRead;
};

// ── Plants ───────────────────────────────────────────────────────────────────
/**
 * Get plants.
 * @param {Pella.ProductionRequestAPI.GetPlantsInput} [input]
 * @returns {Promise<Pella.ProductionRequestAPI.GetPlantsOutput>}
 */
export const getPlants = async (input) => {
  const { data } = await graphqlCall(GRAPHQL_URL, queries.GET_PLANTS, { input });
  return data.productionRequestQuery.getPlants;
};
/**
 * Create plant.
 * @param {Pella.ProductionRequestAPI.CreatePlantInput} input
 * @returns {Promise<Pella.ProductionRequestAPI.Plant>}
 */
export const createPlant = async (input) => {
  const { data } = await graphqlCall(GRAPHQL_URL, queries.CREATE_PLANT, { input });
  return data.productionRequestMutation.createPlant;
};
/**
 * Update plant.
 * @param {Pella.ProductionRequestAPI.Plant['id']} id
 * @param {Pella.ProductionRequestAPI.UpdatePlantInput} input
 * @returns {Promise<Pella.ProductionRequestAPI.Plant>}
 */
export const updatePlant = async (id, input) => {
  const { data } = await graphqlCall(GRAPHQL_URL, queries.UPDATE_PLANT, { id, input });
  return data.productionRequestMutation.updatePlantById;
};
/**
 * Delete plant.
 * @param {Pella.ProductionRequestAPI.Plant['id']} id
 * @returns {Promise<Pella.ProductionRequestAPI.Plant>}
 */
export const deletePlant = async (id) => {
  const { data } = await graphqlCall(GRAPHQL_URL, queries.DELETE_PLANT, { id });
  return data.productionRequestMutation.deletePlantById;
};

// ── Locations ────────────────────────────────────────────────────────────────
/**
 * Get locations by plant.
 * @param {string} id - Plant ID
 * @param {Pella.ProductionRequestAPI.GetLocationsByPlantInput} [input]
 * @returns {Promise<Pella.ProductionRequestAPI.GetLocationsByPlantOutput>}
 */
export const getLocationsByPlant = async (id, input) => {
  const { data } = await graphqlCall(GRAPHQL_URL, queries.GET_LOCATIONS_BY_PLANT, { id, input });
  return data.productionRequestQuery.getLocationsByPlantId;
};
/**
 * Create location.
 * @param {Pella.ProductionRequestAPI.CreateLocationInput} input
 * @returns {Promise<Pella.ProductionRequestAPI.Location>}
 */
export const createLocation = async (input) => {
  const { data } = await graphqlCall(GRAPHQL_URL, queries.CREATE_LOCATION, { input });
  return data.productionRequestMutation.createLocation;
};
/**
 * Update location.
 * @param {Pella.ProductionRequestAPI.Location['id']} id
 * @param {Pella.ProductionRequestAPI.UpdateLocationInput} input
 * @returns {Promise<Pella.ProductionRequestAPI.Location>}
 */
export const updateLocation = async (id, input) => {
  const { data } = await graphqlCall(GRAPHQL_URL, queries.UPDATE_LOCATION, { id, input });
  return data.productionRequestMutation.updateLocationById;
};
/**
 * Delete location.
 * @param {Pella.ProductionRequestAPI.Location['id']} id
 * @returns {Promise<Pella.ProductionRequestAPI.Location>}
 */
export const deleteLocation = async (id) => {
  const { data } = await graphqlCall(GRAPHQL_URL, queries.DELETE_LOCATION, { id });
  return data.productionRequestMutation.deleteLocationById;
};

// ── Actions ──────────────────────────────────────────────────────────────────
/**
 * Get actions by location.
 * @param {string} id - Location ID
 * @param {Pella.ProductionRequestAPI.GetActionsByLocationInput} [input]
 * @returns {Promise<Pella.ProductionRequestAPI.GetActionsByLocationOutput>}
 */
export const getActionsByLocation = async (id, input) => {
  const { data } = await graphqlCall(GRAPHQL_URL, queries.GET_ACTIONS_BY_LOCATION, { id, input });
  return data.productionRequestQuery.getActionsByLocationId;
};
/**
 * Delete action.
 * @param {Pella.ProductionRequestAPI.Action['id']} id
 * @returns {Promise<Pella.ProductionRequestAPI.Action>}
 */
export const deleteAction = async (id) => {
  const { data } = await graphqlCall(GRAPHQL_URL, queries.DELETE_ACTION, { id });
  return data.productionRequestMutation.deleteActionById;
};

// ── Resources ────────────────────────────────────────────────────────────────
/**
 * Get resources by plant.
 * @param {Pella.ProductionRequestAPI.Plant['id']} id
 * @param {boolean} [resourceQueues] - include nested resourceQueues
 * @param {Pella.ProductionRequestAPI.GetResourcesInput} [input]
 * @returns {Promise<Pella.ProductionRequestAPI.GetResourcesByPlantOutput>}
 */
export const getResourcesByPlant = async (id, resourceQueues = false, input = {}) => {
  const { data } = await graphqlCall(GRAPHQL_URL, queries.getResourcesByPlantQuery(resourceQueues), { id, input });
  return data.productionRequestQuery.getResourcesByPlantId;
};
/**
 * Create resource.
 * @param {Pella.ProductionRequestAPI.CreateResourceInput} input
 * @returns {Promise<Pella.ProductionRequestAPI.Resource>}
 */
export const createResource = async (input) => {
  const { data } = await graphqlCall(GRAPHQL_URL, queries.CREATE_RESOURCE, { input });
  return data.productionRequestMutation.createResource;
};
/**
 * Update resource.
 * @param {Pella.ProductionRequestAPI.Resource['id']} id
 * @param {Pella.ProductionRequestAPI.UpdateResourceInput} input
 * @returns {Promise<Pella.ProductionRequestAPI.Resource>}
 */
export const updateResource = async (id, input) => {
  const { data } = await graphqlCall(GRAPHQL_URL, queries.UPDATE_RESOURCE, { id, input });
  return data.productionRequestMutation.updateResourceById;
};
/**
 * Delete resource.
 * @param {Pella.ProductionRequestAPI.Resource['id']} id
 * @returns {Promise<Pella.ProductionRequestAPI.Resource>}
 */
export const deleteResource = async (id) => {
  const { data } = await graphqlCall(GRAPHQL_URL, queries.DELETE_RESOURCE, { id });
  return data.productionRequestMutation.deleteResourceById;
};

// ── Queues ───────────────────────────────────────────────────────────────────
/**
 * Get queues by plant.
 * @param {Pella.ProductionRequestAPI.Plant['id']} id
 * @param {boolean} [resourceQueues] - include nested resourceQueues
 * @param {Pella.ProductionRequestAPI.GetQueuesByPlantInput} [input]
 * @returns {Promise<Pella.ProductionRequestAPI.GetQueuesByPlantOutput>}
 */
export const getQueuesByPlant = async (id, resourceQueues = false, input = {}) => {
  const { data } = await graphqlCall(GRAPHQL_URL, queries.getQueuesByPlantQuery(resourceQueues), { id, input });
  return data.productionRequestQuery.getQueuesByPlantId;
};
/**
 * Create queue.
 * @param {Pella.ProductionRequestAPI.CreateQueueInput} input
 * @returns {Promise<Pella.ProductionRequestAPI.Queue>}
 */
export const createQueue = async (input) => {
  const { data } = await graphqlCall(GRAPHQL_URL, queries.CREATE_QUEUE, { input });
  return data.productionRequestMutation.createQueue;
};
/**
 * Update queue.
 * @param {Pella.ProductionRequestAPI.Queue['id']} id
 * @param {Pella.ProductionRequestAPI.UpdateQueueInput} input
 * @returns {Promise<Pella.ProductionRequestAPI.Queue>}
 */
export const updateQueue = async (id, input) => {
  const { data } = await graphqlCall(GRAPHQL_URL, queries.UPDATE_QUEUE, { id, input });
  return data.productionRequestMutation.updateQueueById;
};
/**
 * Delete queue.
 * @param {Pella.ProductionRequestAPI.Queue['id']} id
 * @returns {Promise<Pella.ProductionRequestAPI.Queue>}
 */
export const deleteQueue = async (id) => {
  const { data } = await graphqlCall(GRAPHQL_URL, queries.DELETE_QUEUE, { id });
  return data.productionRequestMutation.deleteQueueById;
};
/**
 * Get queues by resource.
 * @param {Pella.ProductionRequestAPI.Resource['id']} resourceId
 * @returns {Promise<Pella.ProductionRequestAPI.GetQueuesByResourceOutput>}
 */
export const getQueuesByResource = async (resourceId) => {
  const { data } = await graphqlCall(GRAPHQL_URL, queries.GET_QUEUES_BY_RESOURCE, { resourceId });
  return data.productionRequestQuery.getQueuesByResourceId;
};

// ── Resource queues ──────────────────────────────────────────────────────────
/**
 * Get resource queues.
 * @param {Pella.ProductionRequestAPI.GetResourcesQueuesInput} [input]
 * @returns {Promise<Pella.ProductionRequestAPI.GetResourcesQueuesOutput>}
 */
export const getResourceQueues = async (input) => {
  const { data } = await graphqlCall(GRAPHQL_URL, queries.GET_RESOURCE_QUEUES, { input });
  return data.productionRequestQuery.getResourceQueues;
};
/**
 * Create resource queue.
 * @param {Pella.ProductionRequestAPI.CreateResourceQueueInput} input
 * @returns {Promise<Pella.ProductionRequestAPI.ResourceQueueMutationOutput>}
 */
export const createResourceQueue = async (input) => {
  const { data } = await graphqlCall(GRAPHQL_URL, queries.CREATE_RESOURCE_QUEUE, { input });
  return data.productionRequestMutation.createResourceQueue;
};
/**
 * Update resource queue.
 * @param {Pella.ProductionRequestAPI.Queue['id']} queueId
 * @param {Pella.ProductionRequestAPI.Resource['id']} resourceId
 * @param {Pella.ProductionRequestAPI.UpdateResourceQueueInput} input
 * @returns {Promise<Pella.ProductionRequestAPI.ResourceQueueMutationOutput>}
 */
export const updateResourceQueue = async (queueId, resourceId, input) => {
  const { data } = await graphqlCall(GRAPHQL_URL, queries.UPDATE_RESOURCE_QUEUE, { queueId, resourceId, input });
  return data.productionRequestMutation.updateResourceQueueById;
};
/**
 * Delete resource queue.
 * @param {Pella.ProductionRequestAPI.DeleteResourceQueueInput} variables
 * @returns {Promise<Pella.ProductionRequestAPI.ResourceQueueMutationOutput>}
 */
export const deleteResourceQueue = async (variables) => {
  const { data } = await graphqlCall(GRAPHQL_URL, queries.DELETE_RESOURCE_QUEUE, variables);
  return data.productionRequestMutation.deleteResourceQueueById;
};

// ── Requests ─────────────────────────────────────────────────────────────────
/**
 * Get requests by resource.
 * @param {Pella.ProductionRequestAPI.Resource['id']} resourceId
 * @param {string} [status]
 * @returns {Promise<Pella.ProductionRequestAPI.GetRequestsOutput>}
 */
export const getRequestsByResource = async (resourceId, status) => {
  const { data } = await graphqlCall(GRAPHQL_URL, queries.GET_REQUESTS_BY_RESOURCE, { resourceId, status });
  return data.productionRequestQuery.getRequestsByResourceId;
};
/**
 * Get completed requests by resource.
 * @param {Pella.ProductionRequestAPI.Resource['id']} resourceId
 * @returns {Promise<Pella.ProductionRequestAPI.GetCompletedRequestsOutput>}
 */
export const getCompletedRequestsByResource = async (resourceId) => {
  const { data } = await graphqlCall(GRAPHQL_URL, queries.GET_COMPLETED_REQUESTS_BY_RESOURCE, { resourceId });
  return data.productionRequestQuery.getCompletedRequestsByResourceId;
};
/**
 * Get requests by location.
 * @param {Pella.ProductionRequestAPI.Location['id']} locationId
 * @returns {Promise<Pella.ProductionRequestAPI.GetRequestsOutput>}
 */
export const getRequestsByLocation = async (locationId) => {
  const { data } = await graphqlCall(GRAPHQL_URL, queries.GET_REQUESTS_BY_LOCATION, { locationId });
  return data.productionRequestQuery.getRequestsByLocationIdByStatus;
};
/**
 * Get completed requests by location.
 * @param {Pella.ProductionRequestAPI.Location['id']} locationId
 * @returns {Promise<Pella.ProductionRequestAPI.GetCompletedRequestsOutput>}
 */
export const getCompletedRequestsByLocation = async (locationId) => {
  const { data } = await graphqlCall(GRAPHQL_URL, queries.GET_COMPLETED_REQUESTS_BY_LOCATION, { locationId });
  return data.productionRequestQuery.getCompletedRequestsByLocationIdByStatus;
};
/**
 * Create request.
 * @param {Pella.ProductionRequestAPI.CreateRequestInput} input
 * @returns {Promise<Pella.ProductionRequestAPI.CreateRequestOutput>}
 */
export const createRequest = async (input) => {
  const { data } = await graphqlCall(GRAPHQL_URL, queries.CREATE_REQUEST, { input });
  return data.productionRequestMutation.createRequest;
};
/**
 * Update request.
 * @param {Pella.ProductionRequestAPI.Request['id']} id
 * @param {Pella.ProductionRequestAPI.UpdateRequestInput} input
 * @returns {Promise<Pella.ProductionRequestAPI.UpdateRequestOutput>}
 */
export const updateRequest = async (id, input) => {
  const { data } = await graphqlCall(GRAPHQL_URL, queries.UPDATE_REQUEST, { id, input });
  return data.productionRequestMutation.updateRequestById;
};

// ── Selection lists ──────────────────────────────────────────────────────────
/**
 * Get selection lists by plant.
 * @param {Pella.ProductionRequestAPI.Plant['id']} id
 * @returns {Promise<Pella.ProductionRequestAPI.SelectionList[]>}
 */
export const getSelectionListsByPlant = async (id) => {
  const { data } = await graphqlCall(GRAPHQL_URL, queries.GET_SELECTION_LISTS_BY_PLANT, { id });
  return data.productionRequestQuery.getSelectionListsByPlantId;
};
/**
 * Create selection list.
 * @param {Pella.ProductionRequestAPI.CreateSelectionListInput} input
 * @returns {Promise<Pella.ProductionRequestAPI.SelectionList>}
 */
export const createSelectionList = async (input) => {
  const { data } = await graphqlCall(GRAPHQL_URL, queries.CREATE_SELECTION_LIST, { input });
  return data.productionRequestMutation.createSelectionList;
};
/**
 * Update selection list.
 * @param {Pella.ProductionRequestAPI.SelectionList['id']} id
 * @param {Pella.ProductionRequestAPI.UpdateSelectionListInput} input
 * @returns {Promise<Pella.ProductionRequestAPI.SelectionList>}
 */
export const updateSelectionList = async (id, input) => {
  const { data } = await graphqlCall(GRAPHQL_URL, queries.UPDATE_SELECTION_LIST, { id, input });
  return data.productionRequestMutation.updateSelectionListById;
};
/**
 * Delete selection list.
 * @param {Pella.ProductionRequestAPI.SelectionList['id']} id
 * @returns {Promise<Pella.ProductionRequestAPI.SelectionList>}
 */
export const deleteSelectionList = async (id) => {
  const { data } = await graphqlCall(GRAPHQL_URL, queries.DELETE_SELECTION_LIST, { id });
  return data.productionRequestMutation.deleteSelectionListById;
};
/**
 * Create selection list value.
 * @param {Pella.ProductionRequestAPI.CreateSelectionListValueInput} input
 * @returns {Promise<Pella.ProductionRequestAPI.SelectionList>}
 */
export const createSelectionListValue = async (input) => {
  const { data } = await graphqlCall(GRAPHQL_URL, queries.CREATE_SELECTION_LIST_VALUE, { input });
  return data.productionRequestMutation.createSelectionListValue;
};
/**
 * Update selection list value.
 * @param {Pella.ProductionRequestAPI.SelectionList['id']} id
 * @param {Pella.ProductionRequestAPI.UpdateSelectionListInput} input
 * @returns {Promise<Pella.ProductionRequestAPI.SelectionList>}
 */
export const updateSelectionListValue = async (id, input) => {
  const { data } = await graphqlCall(GRAPHQL_URL, queries.UPDATE_SELECTION_LIST_VALUE, { id, input });
  return data.productionRequestMutation.updateSelectionListValueById;
};
/**
 * Delete selection list value.
 * @param {Pella.ProductionRequestAPI.SelectionList['id']} id
 * @returns {Promise<Pella.ProductionRequestAPI.SelectionList>}
 */
export const deleteSelectionListValue = async (id) => {
  const { data } = await graphqlCall(GRAPHQL_URL, queries.DELETE_SELECTION_LIST_VALUE, { id });
  return data.productionRequestMutation.deleteSelectionListValueById;
};

// ── Transactions and inventory ───────────────────────────────────────────────
/**
 * Get dispositions by plant id.
 * @param {string} id - Plant ID
 * @returns {Promise<Pella.ProductionRequestAPI.GetDispositionsByPlantIdOutput>}
 */
export const getDispositionsByPlantId = async (id) => {
  const { data } = await graphqlCall(GRAPHQL_URL, queries.GET_DISPOSITIONS_BY_PLANT, { id });
  return data.productionRequestQuery.getDispositionsByPlantId;
};
/**
 * Get carrier list.
 * @returns {Promise<Pella.ProductionRequestAPI.GetCarrierListOutput>}
 */
export const getCarrierList = async () => {
  const { data } = await graphqlCall(GRAPHQL_URL, queries.GET_CARRIER_LIST);
  return data.productionRequestQuery.getCarrierList;
};
/**
 * Create trailer to dock transaction.
 * @param {Pella.ProductionRequestAPI.CreateTrailerToDockTransactionInput} input
 * @returns {Promise<string>}
 */
export const createTrailerToDockTransaction = async (input) => {
  const { data } = await graphqlCall(GRAPHQL_URL, queries.CREATE_TRAILER_TO_DOCK_TRANSACTION, { input });
  return data.productionRequestMutation.createTrailerToDockTransaction;
};
/**
 * Get inventory item id by plant.
 * @param {Pella.ProductionRequestAPI.GetInventoryItemIdByPlantInput} input
 * @returns {Promise<Pella.ProductionRequestAPI.GetInventoryItemIdByPlantOutput[]>}
 */
export const getInventoryItemIdByPlant = async ({ organizationId, item }) => {
  const { data } = await graphqlCall(GRAPHQL_URL, queries.GET_INVENTORY_ITEM_ID_BY_PLANT, { organizationId, item });
  return data.productionRequestQuery.getInventoryItemIdByPlant;
};
/**
 * Get subinventory codes by plant.
 * @param {Local.GetSubinventoryCodesByPlantInput} input
 * @returns {Promise<Pella.ProductionRequestAPI.GetSubinventoryCodesByPlantOutput[]>}
 */
export const getSubinventoryCodesByPlant = async (input) => {
  const { data } = await graphqlCall(GRAPHQL_URL, queries.GET_SUBINVENTORY_CODES_BY_PLANT, input);
  return data.productionRequestQuery.getSubinventoryCodesByPlant;
};
/**
 * Get subinventory info by plant.
 * @param {Pella.ProductionRequestAPI.GetSubinventoryInfoByPlantInput} input
 * @returns {Promise<Pella.ProductionRequestAPI.GetSubinventoryInfoByPlantOutput[]>}
 */
export const getSubinventoryInfoByPlant = async ({ organizationId, inventoryItemId, subinventoryCode }) => {
  const { data } = await graphqlCall(GRAPHQL_URL, queries.GET_SUBINVENTORY_INFO_BY_PLANT, {
    organizationId,
    inventoryItemId,
    subinventoryCode,
  });
  return data.productionRequestQuery.getSubinventoryInfoByPlant;
};
/**
 * Get locators.
 * @param {Local.GetLocatorsInput} input
 * @returns {Promise<Local.GetLocatorsOutput>}
 */
export const getLocators = async ({ organizationId, subinventoryCode, inventoryItemId }) => {
  const { data } = await graphqlCall(GRAPHQL_URL, queries.GET_LOCATORS, {
    organizationId,
    subinventoryCode,
    inventoryItemId,
  });
  return data.productionRequestQuery.getLocators;
};
